/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import {
  CalculatorResult,
  ClosedSystemCalculatorData,
  ClosedSystemCalculatorResult,
  FreeEvaporationCalculatorData,
  FreeEvaporationCalculatorResult,
  getValidityState,
  MethodType,
  OpenSystemCalculatorData,
  OpenSystemCalculatorResult,
  ParameterModifier,
  ParameterName,
} from './calculatorTypes';
import * as calc from './old_CalculatorFunctions';

class Calculator {
  private static _instance: Calculator;
  public static get instance() {
    return this._instance || (this._instance = new this());
  }

  /**
   * Recalculates all parameter values based on one known parameter.
   *
   * @param {CalculatorResult|undefined} previousResult - The parameters before the calculation, undefined if this is the first calculation
   * @param {MethodType} method - The method in use (e.g. closed system)
   * @param {number} cardIndex - Index number of the card of the changed parameter (0 is first)
   * @param {ParameterName} changedParameter - The name of the parameter whose value was changed
   * @param {ParameterModifier} value - The new value of the parameter's element
   * @param {number|undefined} changedParameterPhase - Phase of the changed parameter (e.g. pressure 0 is different than pressure 1)
   *
   * @returns {CalculatorResult|undefined} - The newly computed values, or null if failed to calculate (not expected to happen)
   */

  public calculate = (
    previousResult: CalculatorResult | undefined,
    method: MethodType,
    cardIndex: number,
    changedParameter: ParameterName,
    value: ParameterModifier,
    changedParameterPhase?: number
  ): CalculatorResult | undefined => {
    if (previousResult === undefined) {
      return undefined;
    }

    let result: any;
    let calculatorData: any;
    const card0: any = previousResult.calculatorData.cards[0];
    const card1: any = previousResult.calculatorData.cards[1];
    const card2: any = previousResult.calculatorData.cards[2];
    const card3: any = previousResult.calculatorData.cards[3];
    const changed = `${changedParameter}${
      changedParameterPhase === 0 || (changedParameterPhase && changedParameterPhase >= 1) ? changedParameterPhase : ''
    }`;

    switch (method) {
      case 'closedSystem':
        result = new ClosedSystemCalculatorResult();
        calculatorData = result.calculatorData as ClosedSystemCalculatorData;

        let graphDataPrecond: any;
        let graphDataCond: any;
        let graphDataDwell: any;
        let graphDataAer: any;

        // get changed data and update card data accordingly
        if (cardIndex === 0) {
          card0[changed] = value;
        } else if (cardIndex === 1) {
          card1[changed] = value;
        } else if (cardIndex === 2) {
          card2[changed] = value;
        } else if (cardIndex === 3) {
          card3[changed] = value;
        }

        // TODO Performance optimization. If nothing is changed, skip calculations
        // Pseudo code: Check if changed value === previous value -> result = previousResult -> return result

        // Allocate graph data points
        const maxResolution = 360;
        const minCardResolution = 10;
        let durations = [];
        let resolutions: number[] = [];

        if (card0['dehumidification']) {
          durations = [card0['duration'], card1['duration'], card2['duration'], card3['duration']];
        } else {
          durations = [card1['duration'], card2['duration'], card3['duration']];
        }

        // Check that each duration is more than minimum card resolution
        if (Math.min(...durations) < minCardResolution) {
          console.error('one of the durations is less than minimum card resolution:', minCardResolution);
          result = previousResult;
          result.validityState = getValidityState('zeroDuration');
          console.log('result = ', result);
          return result;
        }

        const totalDuration = durations.reduce((partialSum, a) => partialSum + a, 0); // s

        // Allocate resolution for minimum cards
        let allocateMin = [false, false, false, false];
        durations.forEach((duration, i) => {
          if (Math.round((maxResolution / totalDuration) * duration) < minCardResolution) allocateMin[i] = true;
        });
        const minAllocations = allocateMin.filter(Boolean).length;
        const minAllocDurations = durations.reduce((partialSum, a, i) => partialSum + a * Number(allocateMin[i]), 0);

        // Allocate resolutions for the cards
        const spareResolution = maxResolution - minAllocations * minCardResolution;
        durations.forEach((duration, i) => {
          resolutions.push(allocateMin[i] ? minCardResolution : Math.round((spareResolution / (totalDuration - minAllocDurations)) * duration));
        });

        if (resolutions.length < 4) resolutions.unshift(0);

        // console.log('Resolutions', resolutions);
        // console.log(
        //   'Total resolution',
        //   resolutions.reduce((partialSum, a) => partialSum + a, 0)
        // );

        // Pre-conditioning
        if (card0['dehumidification']) {
          [graphDataPrecond, card0['dryingRate'], card0['heatingRate']] = calc.dehumidify(
            card0['relativeHumidity0'],
            card0['temperature0'],
            card0['pressure0'],
            card0['relativeHumidity1'],
            card0['temperature1'],
            card0['pressure1'],
            card0['volume'],
            card0['duration'],
            resolutions[0]
          );
        }
        card0['relativeSaturation1'] = card0['relativeHumidity1'];

        Object.keys(calculatorData.preconditioning).forEach((key) => {
          calculatorData.preconditioning[key] = card0[key];
        });

        // Conditioning
        [graphDataCond, card1['relativeHumidity2'], card1['relativeSaturation2'], card1['h2o2Concentration2'], card1['mixtureDewPoint2']] =
          calc.injection(
            card0['volume'],
            card0['relativeHumidity1'],
            card0['temperature1'],
            card0['pressure1'],
            card1['duration'],
            card1['injectionRate'],
            0,
            card1['liquidConcentration'],
            card1['evaporatorEfficiency'],
            card1['decompositionRate'],
            resolutions[1]
          );

        Object.keys(calculatorData.conditioning).forEach((key) => {
          calculatorData.conditioning[key] = card1[key];
        });

        // Dwell
        [graphDataDwell, card2['relativeHumidity3'], card2['relativeSaturation3'], card2['h2o2Concentration3'], card2['mixtureDewPoint3']] =
          calc.injection(
            card0['volume'],
            card1['relativeHumidity2'],
            card0['temperature1'],
            card0['pressure1'],
            card2['duration'],
            card2['injectionRate'],
            card1['h2o2Concentration2'],
            card1['liquidConcentration'],
            card1['evaporatorEfficiency'],
            card1['decompositionRate'],
            resolutions[2]
          );

        Object.keys(calculatorData.dwell).forEach((key) => {
          calculatorData.dwell[key] = card2[key];
        });

        // calculatorData.dwell.duration = card2['duration'];
        // calculatorData.dwell.injectionRate = card2['injectionrate'];
        // calculatorData.dwell.relativeHumidity = card2['relativeHumidity'];
        // calculatorData.dwell.relativeSaturation = card2['relativeSaturation'];
        // calculatorData.dwell.h2o2Concentration = card2['h2o2Concentration'];
        // calculatorData.dwell.mixtureDewPoint = card2['mixtureDewPoint'];

        // Aeration
        [graphDataAer, card3['relativeHumidity4'], card3['relativeSaturation4'], card3['h2o2Concentration4'], card3['mixtureDewPoint4']] =
          calc.aeration(
            card0['volume'],
            card2['relativeHumidity3'],
            card0['temperature1'],
            card0['pressure1'],
            card3['duration'],
            card2['h2o2Concentration3'],
            card1['decompositionRate'],
            card3['ventilationRate'],
            card3['freshAirRelativeHumidity'],
            card3['freshAirTemperature'],
            // TODO: replace with catalytic converter flow
            card3['catalysatorEfficiency'],
            resolutions[3]
          );

        Object.keys(calculatorData.aeration).forEach((key) => {
          calculatorData.aeration[key] = card3[key];
        });

        // Check if condensation was reached
        const maxRs = Math.max(
          card0['relativeSaturation1'],
          card1['relativeSaturation2'],
          card2['relativeSaturation3'],
          card3['relativeSaturation4']
        );
        if (maxRs === 100) {
          result.validityState = getValidityState('condensation');
        } else if (maxRs > 90) {
          result.validityState = getValidityState('condensationNear');
        }

        console.log('graphdataPrecond', graphDataPrecond);

        const labelsPrecond = graphDataPrecond
          ? graphDataPrecond.seconds.map((item: any) => {
              return {
                time: item,
                phase: 1,
              };
            })
          : [
              {
                time: 0,
                phase: 1,
              },
            ];
        const labelsCond = graphDataCond.seconds.map((item: any) => {
          const addition = labelsPrecond ? labelsPrecond[labelsPrecond.length - 1]?.time : 0;
          return {
            time: item + addition,
            phase: 2,
          };
        });
        const labelsDwell = graphDataDwell.seconds.map((item: any) => {
          const addition = labelsCond[labelsCond.length - 1]?.time;
          return {
            time: item + addition,
            phase: 3,
          };
        });
        const labelsAer = graphDataAer.seconds.map((item: any) => {
          const addition = labelsDwell[labelsDwell.length - 1]?.time;
          return {
            time: item + addition,
            phase: 4,
          };
        });
        const labels = labelsPrecond ? [labelsPrecond, labelsCond, labelsDwell, labelsAer].flat() : [labelsCond, labelsDwell, labelsAer].flat();

        const data = [
          {
            label: 'h2o2Concentration',
            yAxisID: 'y',
            data: graphDataPrecond
              ? [
                  graphDataPrecond.h2o2Concentration,
                  graphDataCond.h2o2Concentration,
                  graphDataDwell.h2o2Concentration,
                  graphDataAer.h2o2Concentration,
                ].flat()
              : [graphDataCond.h2o2Concentration, graphDataDwell.h2o2Concentration, graphDataAer.h2o2Concentration].flat(),
          },
          {
            label: 'relativeHumidity',
            yAxisID: 'y1',
            data: graphDataPrecond
              ? [
                  graphDataPrecond.relativeHumidity,
                  graphDataCond.relativeHumidity,
                  graphDataDwell.relativeHumidity,
                  graphDataAer.relativeHumidity,
                ].flat()
              : [graphDataCond.relativeHumidity, graphDataDwell.relativeHumidity, graphDataAer.relativeHumidity].flat(),
          },
          {
            label: 'relativeSaturation',
            yAxisID: 'y1',
            data: graphDataPrecond
              ? [
                  graphDataPrecond.relativeSaturation,
                  graphDataCond.relativeSaturation,
                  graphDataDwell.relativeSaturation,
                  graphDataAer.relativeSaturation,
                ].flat()
              : [graphDataCond.relativeSaturation, graphDataDwell.relativeSaturation, graphDataAer.relativeSaturation].flat(),
          },
        ];

        result.graphData = {
          labels: labels,
          data: data,
        };

        //console.log('graphData', result.graphData);

        break;

      case 'openSystem':
        result = new OpenSystemCalculatorResult();
        calculatorData = result.calculatorData as OpenSystemCalculatorData;

        const lh2o2Open = changed === 'h2o2Concentration' ? (value as number) : card0['h2o2Concentration'];
        const tOpen = changed === 'temperature' ? (value as number) : card0['temperature'];
        const pOpen = changed === 'pressure' ? (value as number) : card0['pressure'];
        const gFlowOpen = changed === 'flowRate0' ? (value as number) : card0['flowRate0'];
        const rhOpen = changed === 'relativeHumidity0' ? (value as number) : card0['relativeHumidity0'];
        const lFlowOpen = changed === 'flowRate1' ? (value as number) : card0['flowRate1'];

        const outputsOpen = calc.openSystem(lh2o2Open, tOpen, pOpen, gFlowOpen, rhOpen, lFlowOpen);
        // Open system validity states
        if (outputsOpen[1] >= 100) {
          result.validityState = getValidityState('condensation');
        } else if (outputsOpen[1] > 90) {
          result.validityState = getValidityState('condensationNear');
        }
        // console.log(outputsOpen);
        calculatorData.calculation.h2o2Concentration = lh2o2Open;
        calculatorData.calculation.temperature = tOpen;
        calculatorData.calculation.pressure = pOpen;
        calculatorData.calculation.flowRate0 = gFlowOpen;
        calculatorData.calculation.relativeHumidity0 = rhOpen;
        calculatorData.calculation.flowRate1 = lFlowOpen;
        calculatorData.calculation.relativeHumidity1 = outputsOpen[0];
        calculatorData.calculation.relativeSaturation = outputsOpen[1];
        calculatorData.calculation.VH2O2Concentration = outputsOpen[2];

        break;

      case 'freeEvaporation':
        result = new FreeEvaporationCalculatorResult();
        calculatorData = result.calculatorData as FreeEvaporationCalculatorData;

        const tFree = changed === 'temperature' ? (value as number) : card0['temperature'];
        const pFree = changed === 'pressure' ? (value as number) : card0['pressure'];
        const lh2o2Free = changed === 'liquidH2O2Concentration' ? (value as number) : card0['liquidH2O2Concentration'];

        const outputsFree = calc.freeEvaporation(tFree, pFree, lh2o2Free);

        calculatorData.calculation.temperature = tFree;
        calculatorData.calculation.pressure = pFree;
        calculatorData.calculation.liquidH2O2Concentration = lh2o2Free;
        calculatorData.calculation.relativeHumidity = outputsFree[0];
        calculatorData.calculation.relativeSaturation = outputsFree[1];
        calculatorData.calculation.h2o2Concentration = outputsFree[2];

        console.log('result', result);

        break;
    }

    console.log('Calculator.calculate(): result = ', result);
    return result;
  };
}

export default Calculator.instance;
